import React from "react";

import ObjetivoV2AccordionCard from "./ObjetivoV2AccordionCard";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Autocomplete from "@mui/material/Autocomplete";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

export default function ObjetivoV2Equipas({
  isEdicao,
  equipas,
  setEquipas,
  catalogoVendedores,
  vendedores,
}) {
  const adicionarEquipa = () => {
    if (equipas.length > 0) {
      return;
    }

    setEquipas([
      {
        id: null,
        nome: "OUTROS",
        valor: "",
        vendedores: [],
      },
    ]);
  };

  const alterarEquipa = (index, campo, valor) => {
    setEquipas((prev) =>
      prev.map((equipa, i) =>
        i === index
          ? {
              ...equipa,
              [campo]: valor,
            }
          : equipa
      )
    );
  };

  const removerEquipa = (index) => {
    setEquipas((prev) => prev.filter((_, i) => i !== index));
  };

  const obterRefVendedor = (item) => String(item?.refvendedor || item?.ref || "").trim();

  const limparTel = (nome) => {
    const s = String(nome ?? "");

    return s.replace(/\s*(TEL\.?:|TL:).*$/i, "").trim();
  };

  const obterNomeVendedor = (item) => limparTel(item?.vendedor || item?.nome || "");

  const refsIndividuais = new Set(
    (Array.isArray(vendedores) ? vendedores : [])
      .filter((vendedor) => Number(vendedor?.valor || 0) > 0)
      .map((vendedor) => obterRefVendedor(vendedor))
      .filter(Boolean)
  );

  const catalogoDisponivel = (Array.isArray(catalogoVendedores) ? catalogoVendedores : []).filter(
    (vendedor) => !refsIndividuais.has(obterRefVendedor(vendedor))
  );

  return (
    <ObjetivoV2AccordionCard
      titulo="Equipas"
      subtitulo="Equipa opcional para vendedores sem objetivo individual."
    >
      <MDBox
        display="flex"
        alignItems={{
          xs: "stretch",
          sm: "center",
        }}
        justifyContent="space-between"
        flexDirection={{
          xs: "column",
          sm: "row",
        }}
        gap={1}
        mb={2}
      >
        <MDBox />

        {equipas.length === 0 && (
          <MDButton
            color="info"
            startIcon={<AddIcon />}
            onClick={adicionarEquipa}
            disabled={!isEdicao}
            sx={{
              width: {
                xs: "100%",
                sm: "auto",
              },
            }}
          >
            Adicionar equipa
          </MDButton>
        )}
      </MDBox>

      {equipas.length === 0 ? (
        <MDBox minHeight={90} display="flex" alignItems="center" justifyContent="center">
          <MDTypography variant="button" color="text" textAlign="center">
            Não existe uma equipa configurada.
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox display="flex" flexDirection="column" gap={2}>
          {equipas.map((equipa, index) => (
            <Card key={`equipa-${equipa?.id || "nova"}-${index}`} variant="outlined">
              <MDBox p={2}>
                <MDBox
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    md: "minmax(0, 1fr) 180px auto",
                  }}
                  gap={2}
                  alignItems="start"
                >
                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                      Nome da equipa
                    </MDTypography>

                    <MDInput
                      fullWidth
                      value={equipa?.nome || ""}
                      onChange={(e) => alterarEquipa(index, "nome", e.target.value)}
                      onBlur={(e) =>
                        alterarEquipa(
                          index,
                          "nome",
                          String(e.target.value || "")
                            .trim()
                            .toUpperCase()
                        )
                      }
                    />
                  </MDBox>

                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                      Valor
                    </MDTypography>

                    <MDInput
                      type="number"
                      fullWidth
                      value={equipa?.valor ?? ""}
                      onChange={(e) => {
                        const valor = e.target.value;

                        alterarEquipa(
                          index,
                          "valor",
                          valor === "" ? "" : Math.max(0, parseInt(valor, 10) || 0)
                        );
                      }}
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                    />
                  </MDBox>

                  <MDBox
                    display="flex"
                    justifyContent={{
                      xs: "flex-end",
                      md: "center",
                    }}
                    alignItems="center"
                    minHeight={40}
                    mt={{
                      xs: 0,
                      md: 2.5,
                    }}
                  >
                    <Tooltip title="Remover equipa">
                      <IconButton size="small" color="error" onClick={() => removerEquipa(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                <MDBox mt={2}>
                  <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                    Vendedores
                  </MDTypography>

                  <Autocomplete
                    multiple
                    size="small"
                    options={catalogoDisponivel}
                    value={
                      Array.isArray(equipa?.vendedores)
                        ? equipa.vendedores
                            .map((vendedorEquipa) => {
                              const ref = obterRefVendedor(vendedorEquipa);

                              const encontrado = catalogoVendedores.find(
                                (vendedorCatalogo) => obterRefVendedor(vendedorCatalogo) === ref
                              );

                              return encontrado || vendedorEquipa;
                            })
                            .filter((x) => obterRefVendedor(x))
                        : []
                    }
                    getOptionLabel={(option) => {
                      const ref = obterRefVendedor(option);
                      const nome = obterNomeVendedor(option);

                      return nome ? `${ref} - ${nome}` : ref;
                    }}
                    onChange={(_, values) =>
                      alterarEquipa(
                        index,
                        "vendedores",
                        values.map((item) => ({
                          refvendedor: obterRefVendedor(item),
                          vendedor: obterNomeVendedor(item),
                        }))
                      )
                    }
                    isOptionEqualToValue={(option, value) =>
                      obterRefVendedor(option) === obterRefVendedor(value)
                    }
                    disableCloseOnSelect
                    disabled={!isEdicao}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Selecionar vendedores..." />
                    )}
                  />
                </MDBox>
              </MDBox>
            </Card>
          ))}
        </MDBox>
      )}
    </ObjetivoV2AccordionCard>
  );
}
